import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import FilterPanel from './components/FilterPanel';
import ProjectModal from './components/ProjectModal';
import CodeLaboratory from './components/CodeLaboratory';
import ProjectStats from './components/ProjectStats';

const ProjectDimensionsPortfolio = () => {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProject, setSelectedProject] = useState(null);
  const [modalMode, setModalMode] = useState('details');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCodeLabOpen, setIsCodeLabOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    technology: [],
    type: [],
    industry: [],
    complexity: []
  });

  // Mock project data
  const projects = [
    {
      id: 1,
      title: "NeuroCommerce AI Platform",
      category: "E-commerce & AI",
      description: "Revolutionary e-commerce platform powered by neural networks for personalized shopping experiences.",
      fullDescription: `A cutting-edge e-commerce platform that leverages advanced machine learning algorithms to create hyper-personalized shopping experiences. The system analyzes user behavior patterns, purchase history, and real-time interactions to deliver intelligent product recommendations and dynamic pricing strategies.\n\nThe platform features a sophisticated neural network architecture that continuously learns from user interactions, improving recommendation accuracy by 340% compared to traditional systems. Built with scalability in mind, it handles millions of concurrent users while maintaining sub-200ms response times.`,
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop"
      ],
      technologies: ["React", "Node.js", "TensorFlow", "MongoDB", "Redis", "Docker"],
      status: "live",
      featured: true,
      metrics: {
        performance: "98%",
        uptime: "99.9%",
        users: "2.3M"
      },
      detailedMetrics: {
        performance: { value: "98%", label: "Performance Score", description: "Lighthouse audit results" },
        uptime: { value: "99.9%", label: "System Uptime", description: "Last 12 months average" },
        users: { value: "2.3M", label: "Active Users", description: "Monthly active users" },
        conversion: { value: "340%", label: "Conversion Boost", description: "Compared to previous system" },
        loadTime: { value: "1.2s", label: "Load Time", description: "Average page load time" },
        satisfaction: { value: "4.9/5", label: "User Rating", description: "Customer satisfaction score" }
      },
      duration: "8 months",
      teamSize: "12 developers",
      role: "Lead Full-Stack Developer & AI Integration Specialist",
      features: [
        "Real-time personalization engine",
        "Advanced recommendation algorithms",
        "Dynamic pricing optimization",
        "Multi-language support (15 languages)",
        "Progressive Web App capabilities",
        "Advanced analytics dashboard"
      ],
      architecture: `// Microservices Architecture
const services = {
  userService: 'handles authentication & profiles',
  productService: 'manages catalog & inventory',
  recommendationEngine: 'AI-powered suggestions',
  paymentGateway: 'secure transaction processing',
  analyticsService: 'real-time data processing'
};

// Neural Network Integration
const aiModel = new TensorFlow.Sequential({
  layers: [
    tf.layers.dense({units: 128, activation: 'relu'}),
    tf.layers.dropout({rate: 0.3}),
    tf.layers.dense({units: 64, activation: 'relu'}),
    tf.layers.dense({units: 32, activation: 'softmax'})
  ]
});`,
      challenges: [
        {
          problem: "Real-time personalization at scale",
          solution: "Implemented Redis caching with ML model optimization, reducing response time by 75%"
        },
        {
          problem: "Complex recommendation algorithm accuracy",
          solution: "Developed hybrid collaborative-content filtering with deep learning, achieving 94% accuracy"
        },
        {
          problem: "High-traffic performance optimization",
          solution: "Microservices architecture with auto-scaling and CDN integration"
        }
      ],
      codeSnippets: {
        component: `import React, { useState, useEffect } from 'react';
import { useRecommendations } from '../hooks/useRecommendations';

const ProductRecommendations = ({ userId, currentProduct }) => {
  const [recommendations, setRecommendations] = useState([]);
  const { getPersonalizedRecommendations, loading } = useRecommendations();

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        const data = await getPersonalizedRecommendations({
          userId,
          productId: currentProduct.id,
          context: 'product_page'
        });
        setRecommendations(data);
      } catch (error) {
        console.error('Failed to fetch recommendations:', error);
      }
    };

    fetchRecommendations();
  }, [userId, currentProduct.id]);

  return (
    <div className="recommendations-container">
      <h3>Recommended for You</h3>
      {loading ? (
        <RecommendationsSkeleton />
      ) : (
        <div className="recommendations-grid">
          {recommendations.map(product => (
            <ProductCard 
              key={product.id} 
              product={product}
              confidence={product.confidence}
            />
          ))}
        </div>
      )}
    </div>
  );
};`,
        hooks: `import { useState, useCallback } from 'react';
import { aiService } from '../services/aiService';

export const useRecommendations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getPersonalizedRecommendations = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await aiService.getRecommendations({
        ...params,
        algorithm: 'neural_collaborative_filtering',
        includeConfidence: true
      });
      
      return response.data.recommendations;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { getPersonalizedRecommendations, loading, error };
};`,
        styles: `.recommendations-container {
  padding: 2rem;
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 255, 0.3);
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.product-card {
  background: rgba(0, 212, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  transition: all 0.3s ease;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 0 25px rgba(0, 212, 255, 0.4);
}`,
        api: `// AI Recommendation Service
class AIRecommendationService {
  constructor() {
    this.baseURL = process.env.REACT_APP_AI_SERVICE_URL;
    this.model = null;
  }

  async getRecommendations({ userId, productId, context }) {
    const userProfile = await this.getUserProfile(userId);
    const productFeatures = await this.getProductFeatures(productId);
    
    const input = this.preprocessInput({
      userProfile,
      productFeatures,
      context
    });

    const predictions = await this.model.predict(input);
    
    return this.postprocessPredictions(predictions, {
      userId,
      limit: 10,
      diversityFactor: 0.3
    });
  }

  async preprocessInput(data) {
    // Feature engineering and normalization
    return tf.tensor2d([
      ...data.userProfile.preferences,
      ...data.productFeatures.embeddings,
      this.contextToVector(data.context)
    ]);
  }
}`
      },
      architecturePatterns: [
        "Microservices with API Gateway",
        "Event-driven architecture with Kafka",
        "CQRS pattern for read/write separation",
        "Circuit breaker for fault tolerance",
        "Repository pattern with dependency injection"
      ],
      bestPractices: [
        "Test-driven development (95% code coverage)",
        "Continuous integration/deployment pipeline",
        "Code review process with automated quality gates",
        "Performance monitoring and alerting",
        "Security-first development approach"
      ],
      performanceInsights: {
        bundleSize: "245KB",
        loadTime: "1.2s",
        lighthouse: "98/100"
      },
      testimonial: {
        quote: "The AI-powered recommendations increased our conversion rate by 340% and customer satisfaction scores reached an all-time high. The technical execution was flawless.",
        name: "Sarah Chen",
        position: "CTO, TechCommerce Inc.",
        avatar: "https://randomuser.me/api/portraits/women/32.jpg"
      },
      businessImpact: [
        "340% increase in conversion rates",
        "$2.3M additional revenue in first quarter",
        "45% reduction in customer acquisition cost",
        "89% improvement in customer retention"
      ],
      technicalAchievements: [
        "Sub-200ms API response times at scale",
        "99.9% system uptime with zero data loss",
        "Successful migration of 2.3M users with zero downtime",
        "Implementation of advanced ML algorithms in production"
      ]
    },
    {
      id: 2,
      title: "Quantum Finance Dashboard",
      category: "FinTech & Analytics",
      description: "Next-generation financial analytics platform with real-time market data and predictive modeling.",
      fullDescription: `A sophisticated financial analytics platform that combines real-time market data processing with advanced predictive modeling capabilities. The system processes over 10 million data points per second from global financial markets, providing traders and analysts with actionable insights through an intuitive, cyberpunk-inspired interface.\n\nThe platform features quantum-inspired algorithms for portfolio optimization, risk assessment, and market trend prediction. Built with high-frequency trading requirements in mind, it delivers microsecond-level latency for critical trading decisions while maintaining enterprise-grade security and compliance standards.`,
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&h=600&fit=crop"
      ],
      technologies: ["Vue.js", "Python", "FastAPI", "PostgreSQL", "WebSocket", "D3.js"],
      status: "live",
      featured: false,
      metrics: {
        performance: "95%",
        uptime: "99.8%",
        users: "850K"
      },
      detailedMetrics: {
        performance: { value: "95%", label: "Performance Score", description: "Real-time processing efficiency" },
        uptime: { value: "99.8%", label: "System Uptime", description: "Financial markets availability" },
        users: { value: "850K", label: "Active Traders", description: "Daily active users" },
        latency: { value: "12μs", label: "Average Latency", description: "Order execution time" },
        accuracy: { value: "87%", label: "Prediction Accuracy", description: "Market trend predictions" },
        volume: { value: "10M", label: "Data Points/sec", description: "Real-time processing capacity" }
      },
      duration: "10 months",
      teamSize: "8 developers",
      role: "Senior Frontend Developer & Data Visualization Specialist",
      features: [
        "Real-time market data streaming",
        "Advanced charting with 50+ indicators",
        "Portfolio optimization algorithms",
        "Risk assessment and alerts",
        "Multi-exchange connectivity",
        "Regulatory compliance reporting"
      ],
      architecture: `// High-Performance Data Pipeline
const marketDataPipeline = {
  ingestion: 'Kafka streams for real-time data',
  processing: 'Apache Spark for complex calculations',
  storage: 'TimescaleDB for time-series data',
  caching: 'Redis for sub-millisecond access',
  delivery: 'WebSocket for real-time updates'
};

// Quantum-Inspired Portfolio Optimization
class QuantumPortfolioOptimizer {
  constructor(assets, constraints) {
    this.assets = assets;
    this.constraints = constraints;
    this.quantumCircuit = this.initializeCircuit();
  }
}`,
      challenges: [
        {
          problem: "Ultra-low latency requirements for trading",
          solution: "Implemented custom WebSocket protocol with binary data compression, achieving 12μs latency"
        },
        {
          problem: "Complex financial data visualization",
          solution: "Created custom D3.js components with WebGL acceleration for smooth 60fps rendering"
        },
        {
          problem: "Regulatory compliance across multiple jurisdictions",
          solution: "Built modular compliance engine with automated reporting and audit trails"
        }
      ],
      codeSnippets: {
        component: `import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useWebSocket } from '../hooks/useWebSocket';

const RealTimeChart = ({ symbol, timeframe }) => {
  const svgRef = useRef();
  const [data, setData] = useState([]);
  const { lastMessage, sendMessage } = useWebSocket();

  useEffect(() => {
    // Subscribe to real-time market data
    sendMessage({
      type: 'SUBSCRIBE',
      symbol,
      timeframe,
      indicators: ['SMA', 'RSI', 'MACD']
    });

    return () => {
      sendMessage({ type: 'UNSUBSCRIBE', symbol });
    };
  }, [symbol, timeframe]);

  useEffect(() => {
    if (lastMessage?.type === 'MARKET_DATA') {
      setData(prevData => {
        const newData = [...prevData, lastMessage.data];
        return newData.slice(-1000); // Keep last 1000 points
      });
    }
  }, [lastMessage]);

  useEffect(() => {
    if (data.length === 0) return;

    const svg = d3.select(svgRef.current);
    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    // Clear previous chart
    svg.selectAll("*").remove();

    const g = svg.append("g")
      .attr("transform", \`translate(\${margin.left},\${margin.top})\`);

    // Scales
    const xScale = d3.scaleTime()
      .domain(d3.extent(data, d => new Date(d.timestamp)))
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain(d3.extent(data, d => d.price))
      .range([height, 0]);

    // Line generator
    const line = d3.line()
      .x(d => xScale(new Date(d.timestamp)))
      .y(d => yScale(d.price))
      .curve(d3.curveMonotoneX);

    // Draw line
    g.append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", "#00d4ff")
      .attr("stroke-width", 2)
      .attr("d", line);

    // Add axes
    g.append("g")
      .attr("transform", \`translate(0,\${height})\`)
      .call(d3.axisBottom(xScale));

    g.append("g")
      .call(d3.axisLeft(yScale));

  }, [data]);

  return (
    <div className="chart-container">
      <svg ref={svgRef} width={800} height={400}></svg>
    </div>
  );
};`,
        hooks: `import { useState, useEffect, useRef } from 'react';

export const useWebSocket = (url = process.env.REACT_APP_WS_URL) => {
  const [socket, setSocket] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const reconnectTimeoutRef = useRef(null);

  const connect = () => {
    try {
      const ws = new WebSocket(url);
      
      ws.onopen = () => {
        setConnectionStatus('Connected');
        setSocket(ws);
        console.log('WebSocket connected');
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);
        setLastMessage(message);
      };

      ws.onclose = () => {
        setConnectionStatus('Disconnected');
        setSocket(null);
        
        // Reconnect after 3 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('Error');
      };

    } catch (error) {
      console.error('Failed to connect:', error);
      setConnectionStatus('Error');
    }
  };

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socket) {
        socket.close();
      }
    };
  }, []);

  const sendMessage = (message) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  };

  return { lastMessage, sendMessage, connectionStatus };
};`,
        styles: `.chart-container {
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

.chart-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.chart-container svg {
  filter: drop-shadow(0 0 10px rgba(0, 212, 255, 0.3));
}

.axis text {
  fill: #cccccc;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
}

.axis path,
.axis line {
  stroke: rgba(0, 212, 255, 0.5);
  stroke-width: 1;
}`,
        api: `// High-Performance Market Data API
class MarketDataService {
  constructor() {
    this.wsConnection = null;
    this.subscriptions = new Map();
    this.dataBuffer = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  async connect() {
    try {
      this.wsConnection = new WebSocket(
        \`\${process.env.REACT_APP_WS_URL}?token=\${await this.getAuthToken()}\`
      );

      this.wsConnection.onopen = () => {
        console.log('Market data connection established');
        this.reconnectAttempts = 0;
        this.resubscribeAll();
      };

      this.wsConnection.onmessage = (event) => {
        const data = this.deserializeMessage(event.data);
        this.processMarketData(data);
      };

      this.wsConnection.onclose = () => {
        this.handleDisconnection();
      };

    } catch (error) {
      console.error('Failed to connect to market data service:', error);
      this.scheduleReconnect();
    }
  }

  subscribe(symbol, callback) {
    const subscriptionId = \`\${symbol}_\${Date.now()}\`;
    this.subscriptions.set(subscriptionId, { symbol, callback });

    if (this.wsConnection?.readyState === WebSocket.OPEN) {
      this.wsConnection.send(JSON.stringify({
        type: 'SUBSCRIBE',
        symbol,
        subscriptionId
      }));
    }

    return subscriptionId;
  }

  processMarketData(data) {
    const { symbol, price, timestamp, volume } = data;
    
    // Update buffer with latest data
    if (!this.dataBuffer.has(symbol)) {
      this.dataBuffer.set(symbol, []);
    }
    
    const buffer = this.dataBuffer.get(symbol);
    buffer.push({ price, timestamp, volume });
    
    // Keep only last 1000 data points
    if (buffer.length > 1000) {
      buffer.shift();
    }

    // Notify subscribers
    this.subscriptions.forEach(({ symbol: subSymbol, callback }) => {
      if (subSymbol === symbol) {
        callback(data);
      }
    });
  }
}`
      },
      architecturePatterns: [
        "Event-driven architecture with message queues",
        "CQRS with event sourcing for audit trails",
        "Microservices with service mesh",
        "Real-time data streaming with Apache Kafka",
        "Circuit breaker pattern for external APIs"
      ],
      bestPractices: [
        "High-frequency trading optimizations",
        "Comprehensive security audit and penetration testing",
        "Real-time monitoring and alerting",
        "Disaster recovery and business continuity planning",
        "Regulatory compliance automation"
      ],
      performanceInsights: {
        bundleSize: "180KB",
        loadTime: "0.8s",
        lighthouse: "95/100"
      },
      testimonial: {
        quote: "The platform's ultra-low latency and sophisticated analytics have given our traders a significant competitive advantage. The technical implementation exceeded all our expectations.",
        name: "Marcus Thompson",
        position: "Head of Trading Technology, QuantumCapital",
        avatar: "https://randomuser.me/api/portraits/men/45.jpg"
      },
      businessImpact: [
        "12μs average order execution latency",
        "87% accuracy in market trend predictions",
        "45% increase in trading volume",
        "$5.2M cost savings through automation"
      ],
      technicalAchievements: [
        "Processing 10M+ data points per second",
        "99.8% uptime during market hours",
        "Zero data loss during system upgrades",
        "Successful integration with 15+ exchanges"
      ]
    },
    {
      id: 3,
      title: "CyberHealth Telemedicine",
      category: "Healthcare & IoT",
      description: "Futuristic telemedicine platform with IoT integration and AI-powered diagnostics.",
      fullDescription: `A revolutionary telemedicine platform that bridges the gap between traditional healthcare and cutting-edge technology. The system integrates with various IoT medical devices to provide real-time health monitoring, AI-powered preliminary diagnostics, and seamless doctor-patient communication through an immersive digital interface.\n\nThe platform features advanced biometric analysis, predictive health modeling, and secure HIPAA-compliant data handling. Built with accessibility in mind, it supports multiple languages and provides specialized interfaces for different user groups including patients, healthcare providers, and medical administrators.`,
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
      gallery: [
        "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=800&h=600&fit=crop"
      ],
      technologies: ["React Native", "Express.js", "MongoDB", "Socket.io", "TensorFlow", "AWS"],
      status: "development",
      featured: true,
      metrics: {
        performance: "92%",
        uptime: "99.5%",
        users: "125K"
      },
      detailedMetrics: {
        performance: { value: "92%", label: "Performance Score", description: "Mobile app performance" },
        uptime: { value: "99.5%", label: "System Uptime", description: "Healthcare service availability" },
        users: { value: "125K", label: "Registered Patients", description: "Active user base" },
        consultations: { value: "50K", label: "Monthly Consultations", description: "Telemedicine sessions" },
        accuracy: { value: "94%", label: "Diagnostic Accuracy", description: "AI preliminary diagnosis" },
        satisfaction: { value: "4.8/5", label: "Patient Satisfaction", description: "User experience rating" }
      },
      duration: "12 months",
      teamSize: "15 developers",
      role: "Mobile App Lead & IoT Integration Specialist",
      features: [
        "Real-time video consultations",
        "IoT device integration (heart rate, blood pressure, glucose)",
        "AI-powered symptom analysis",
        "Electronic health records (EHR) integration",
        "Prescription management system",
        "Multi-language support (12 languages)"
      ],
      architecture: `// Healthcare Microservices Architecture
const healthcareServices = {
  patientService: 'manages patient profiles and history',
  consultationService: 'handles video calls and appointments',
  diagnosticAI: 'processes symptoms and provides insights',
  iotGateway: 'connects and manages medical devices',
  ehrIntegration: 'syncs with hospital systems',
  complianceService: 'ensures HIPAA compliance'
};

// AI Diagnostic Engine
class HealthDiagnosticAI {
  constructor() {
    this.model = tf.loadLayersModel('/models/diagnostic-model.json');
    this.symptoms = new SymptomProcessor();
    this.riskAssessment = new RiskAssessmentEngine();
  }

  async analyzeSymptomsAndVitals(symptoms, vitals, history) {
    const features = this.extractFeatures(symptoms, vitals, history);
    const predictions = await this.model.predict(features);
    return this.interpretPredictions(predictions);
  }
}`,
      challenges: [
        {
          problem: "HIPAA compliance and data security",
          solution: "Implemented end-to-end encryption, audit logging, and zero-trust security architecture"
        },
        {
          problem: "Real-time IoT data processing from multiple devices",
          solution: "Built scalable IoT gateway with device abstraction layer and real-time data pipeline"
        },
        {
          problem: "AI diagnostic accuracy and liability concerns",
          solution: "Developed explainable AI with confidence scores and mandatory doctor review workflows"
        }
      ],
      codeSnippets: {
        component: `import React, { useState, useEffect } from 'react';
import { useIoTDevices } from '../hooks/useIoTDevices';
import { useDiagnosticAI } from '../hooks/useDiagnosticAI';

const VitalSignsMonitor = ({ patientId }) => {
  const [vitals, setVitals] = useState({});
  const [alerts, setAlerts] = useState([]);
  const { devices, connectDevice, getDeviceData } = useIoTDevices();
  const { analyzeVitals, isAnalyzing } = useDiagnosticAI();

  useEffect(() => {
    const interval = setInterval(async () => {
      const currentVitals = {};
      
      for (const device of devices) {
        if (device.connected) {
          const data = await getDeviceData(device.id);
          currentVitals[device.type] = data;
        }
      }

      setVitals(currentVitals);

      // Check for anomalies
      const analysis = await analyzeVitals(currentVitals, patientId);
      if (analysis.alerts.length > 0) {
        setAlerts(prev => [...prev, ...analysis.alerts]);
      }
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [devices, patientId]);

  const VitalCard = ({ type, value, unit, status, trend }) => (
    <div className={\`vital-card \${status}\`}>
      <div className="vital-header">
        <h3>{type}</h3>
        <div className={\`status-indicator \${status}\`} />
      </div>
      <div className="vital-value">
        <span className="value">{value}</span>
        <span className="unit">{unit}</span>
      </div>
      <div className="vital-trend">
        <TrendChart data={trend} />
      </div>
    </div>
  );

  return (
    <div className="vitals-monitor">
      <div className="monitor-header">
        <h2>Real-Time Vital Signs</h2>
        <div className="connection-status">
          {devices.filter(d => d.connected).length} devices connected
        </div>
      </div>

      <div className="vitals-grid">
        {vitals.heartRate && (
          <VitalCard
            type="Heart Rate"
            value={vitals.heartRate.value}
            unit="bpm"
            status={vitals.heartRate.status}
            trend={vitals.heartRate.trend}
          />
        )}
        
        {vitals.bloodPressure && (
          <VitalCard
            type="Blood Pressure"
            value={\`\${vitals.bloodPressure.systolic}/\${vitals.bloodPressure.diastolic}\`}
            unit="mmHg"
            status={vitals.bloodPressure.status}
            trend={vitals.bloodPressure.trend}
          />
        )}

        {vitals.oxygenSaturation && (
          <VitalCard
            type="Oxygen Saturation"
            value={vitals.oxygenSaturation.value}
            unit="%"
            status={vitals.oxygenSaturation.status}
            trend={vitals.oxygenSaturation.trend}
          />
        )}
      </div>

      {alerts.length > 0 && (
        <div className="alerts-panel">
          <h3>Health Alerts</h3>
          {alerts.map((alert, index) => (
            <div key={index} className={\`alert \${alert.severity}\`}>
              <div className="alert-icon">⚠️</div>
              <div className="alert-content">
                <div className="alert-title">{alert.title}</div>
                <div className="alert-message">{alert.message}</div>
                <div className="alert-timestamp">
                  {new Date(alert.timestamp).toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};`,
        hooks: `import { useState, useEffect, useCallback } from 'react';
import { iotService } from '../services/iotService';

export const useIoTDevices = () => {
  const [devices, setDevices] = useState([]);
  const [isScanning, setIsScanning] = useState(false);

  useEffect(() => {
    // Initialize IoT service and discover devices
    const initializeDevices = async () => {
      try {
        const discoveredDevices = await iotService.discoverDevices();
        setDevices(discoveredDevices);
      } catch (error) {
        console.error('Failed to discover IoT devices:', error);
      }
    };

    initializeDevices();

    // Listen for device status changes
    const handleDeviceStatusChange = (deviceId, status) => {
      setDevices(prev => prev.map(device => 
        device.id === deviceId 
          ? { ...device, connected: status === 'connected' }
          : device
      ));
    };

    iotService.on('deviceStatusChange', handleDeviceStatusChange);

    return () => {
      iotService.off('deviceStatusChange', handleDeviceStatusChange);
    };
  }, []);

  const connectDevice = useCallback(async (deviceId) => {
    try {
      setIsScanning(true);
      await iotService.connectDevice(deviceId);
      
      setDevices(prev => prev.map(device => 
        device.id === deviceId 
          ? { ...device, connected: true, lastConnected: new Date() }
          : device
      ));
    } catch (error) {
      console.error(\`Failed to connect device \${deviceId}:\`, error);
      throw error;
    } finally {
      setIsScanning(false);
    }
  }, []);

  const disconnectDevice = useCallback(async (deviceId) => {
    try {
      await iotService.disconnectDevice(deviceId);
      
      setDevices(prev => prev.map(device => 
        device.id === deviceId 
          ? { ...device, connected: false }
          : device
      ));
    } catch (error) {
      console.error(\`Failed to disconnect device \${deviceId}:\`, error);
      throw error;
    }
  }, []);

  const getDeviceData = useCallback(async (deviceId) => {
    try {
      const data = await iotService.getDeviceData(deviceId);
      return data;
    } catch (error) {
      console.error(\`Failed to get data from device \${deviceId}:\`, error);
      return null;
    }
  }, []);

  return {
    devices,
    isScanning,
    connectDevice,
    disconnectDevice,
    getDeviceData
  };
};`,
        styles: `.vitals-monitor {
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 16px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
}

.vitals-monitor::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 70%, rgba(0, 255, 136, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.vitals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
}

.vital-card {
  background: rgba(0, 212, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
}

.vital-card.normal {
  border-color: rgba(0, 255, 136, 0.5);
}

.vital-card.warning {
  border-color: rgba(255, 170, 0, 0.5);
  box-shadow: 0 0 20px rgba(255, 170, 0, 0.2);
}

.vital-card.critical {
  border-color: rgba(255, 68, 68, 0.5);
  box-shadow: 0 0 20px rgba(255, 68, 68, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
}

.vital-value {
  display: flex;
  align-items: baseline;
  margin: 1rem 0;
}

.vital-value .value {
  font-size: 2.5rem;
  font-weight: bold;
  font-family: 'JetBrains Mono', monospace;
  color: #00d4ff;
}

.vital-value .unit {
  font-size: 1rem;
  color: #cccccc;
  margin-left: 0.5rem;
}`,
        api: `// IoT Device Management Service
class IoTDeviceService {
  constructor() {
    this.devices = new Map();
    this.connections = new Map();
    this.dataStreams = new Map();
    this.eventEmitter = new EventEmitter();
  }

  async discoverDevices() {
    try {
      // Scan for Bluetooth LE medical devices
      const bluetoothDevices = await this.scanBluetoothDevices();
      
      // Scan for WiFi-enabled devices
      const wifiDevices = await this.scanWiFiDevices();
      
      // Combine and filter medical devices
      const allDevices = [...bluetoothDevices, ...wifiDevices]
        .filter(device => this.isMedicalDevice(device));

      // Update device registry
      allDevices.forEach(device => {
        this.devices.set(device.id, {
          ...device,
          lastSeen: new Date(),
          connected: false,
          dataHistory: []
        });
      });

      return Array.from(this.devices.values());
    } catch (error) {
      console.error('Device discovery failed:', error);
      throw new Error('Failed to discover IoT devices');
    }
  }

  async connectDevice(deviceId) {
    const device = this.devices.get(deviceId);
    if (!device) {
      throw new Error(\`Device \${deviceId} not found\`);
    }

    try {
      let connection;
      
      if (device.protocol === 'bluetooth') {
        connection = await this.connectBluetooth(device);
      } else if (device.protocol === 'wifi') {
        connection = await this.connectWiFi(device);
      } else {
        throw new Error(\`Unsupported protocol: \${device.protocol}\`);
      }

      this.connections.set(deviceId, connection);
      
      // Start data streaming
      this.startDataStream(deviceId);
      
      // Update device status
      device.connected = true;
      device.lastConnected = new Date();
      
      this.eventEmitter.emit('deviceStatusChange', deviceId, 'connected');
      
      return connection;
    } catch (error) {
      console.error(\`Failed to connect to device \${deviceId}:\`, error);
      throw error;
    }
  }

  async getDeviceData(deviceId) {
    const connection = this.connections.get(deviceId);
    if (!connection) {
      throw new Error(\`Device \${deviceId} not connected\`);
    }

    try {
      const rawData = await connection.readData();
      const processedData = this.processDeviceData(deviceId, rawData);
      
      // Store in history
      const device = this.devices.get(deviceId);
      device.dataHistory.push({
        ...processedData,
        timestamp: new Date()
      });

      // Keep only last 1000 readings
      if (device.dataHistory.length > 1000) {
        device.dataHistory.shift();
      }

      return processedData;
    } catch (error) {
      console.error(\`Failed to read data from device \${deviceId}:\`, error);
      throw error;
    }
  }

  processDeviceData(deviceId, rawData) {
    const device = this.devices.get(deviceId);
    
    switch (device.type) {
      case 'heart_rate_monitor':
        return {
          value: rawData.heartRate,
          unit: 'bpm',
          status: this.assessHeartRate(rawData.heartRate),
          trend: this.calculateTrend(deviceId, 'heartRate')
        };
        
      case 'blood_pressure_monitor':
        return {
          systolic: rawData.systolic,
          diastolic: rawData.diastolic,
          unit: 'mmHg',
          status: this.assessBloodPressure(rawData.systolic, rawData.diastolic),
          trend: this.calculateTrend(deviceId, 'bloodPressure')
        };
        
      case 'pulse_oximeter':
        return {
          value: rawData.oxygenSaturation,
          unit: '%',
          status: this.assessOxygenSaturation(rawData.oxygenSaturation),
          trend: this.calculateTrend(deviceId, 'oxygenSaturation')
        };
        
      default:
        return rawData;
    }
  }
}`
      },
      architecturePatterns: [
        "Hexagonal architecture for healthcare domain",
        "Event sourcing for audit trails and compliance",
        "CQRS with separate read/write models",
        "Microservices with health check endpoints",
        "Circuit breaker for external medical systems"
      ],
      bestPractices: [
        "HIPAA compliance and data encryption",
        "Medical device integration standards (HL7 FHIR)",
        "Accessibility compliance (WCAG 2.1 AA)",
        "Multi-language internationalization",
        "Comprehensive security audit and penetration testing"
      ],
      performanceInsights: {
        bundleSize: "320KB",
        loadTime: "1.5s",
        lighthouse: "92/100"
      },
      testimonial: {
        quote: "This platform has revolutionized how we deliver healthcare services. The IoT integration and AI diagnostics have improved patient outcomes while reducing costs significantly.",
        name: "Dr. Emily Rodriguez",
        position: "Chief Medical Officer, HealthTech Solutions",
        avatar: "https://randomuser.me/api/portraits/women/28.jpg"
      },
      businessImpact: [
        "94% accuracy in AI preliminary diagnostics",
        "60% reduction in unnecessary hospital visits",
        "40% improvement in patient satisfaction scores",
        "$1.8M cost savings through remote monitoring"
      ],
      technicalAchievements: [
        "HIPAA-compliant end-to-end encryption",
        "Real-time IoT data processing from 15+ device types",
        "99.5% uptime for critical healthcare services",
        "Multi-language support for global accessibility"
      ]
    }
  ];

  // Filter configuration
  const filterConfig = {
    technologies: [
      { value: 'React', label: 'React', count: 2 },
      { value: 'Vue.js', label: 'Vue.js', count: 1 },
      { value: 'React Native', label: 'React Native', count: 1 },
      { value: 'Node.js', label: 'Node.js', count: 1 },
      { value: 'Python', label: 'Python', count: 1 },
      { value: 'TensorFlow', label: 'TensorFlow', count: 2 },
      { value: 'MongoDB', label: 'MongoDB', count: 2 },
      { value: 'PostgreSQL', label: 'PostgreSQL', count: 1 }
    ],
    types: [
      { value: 'web-app', label: 'Web Application', count: 2 },
      { value: 'mobile-app', label: 'Mobile Application', count: 1 },
      { value: 'ai-platform', label: 'AI Platform', count: 2 },
      { value: 'dashboard', label: 'Dashboard', count: 1 }
    ],
    industries: [
      { value: 'ecommerce', label: 'E-commerce', count: 1 },
      { value: 'fintech', label: 'FinTech', count: 1 },
      { value: 'healthcare', label: 'Healthcare', count: 1 }
    ],
    complexity: [
      { value: 'high', label: 'High Complexity', count: 3 },
      { value: 'medium', label: 'Medium Complexity', count: 0 },
      { value: 'low', label: 'Low Complexity', count: 0 }
    ]
  };

  // Portfolio statistics
  const portfolioStats = {
    totalProjects: 47,
    technologiesUsed: 25,
    happyClients: 32,
    averageUptime: "99.7%"
  };

  // Filter projects based on active filters
  const filteredProjects = projects.filter(project => {
    return Object.entries(activeFilters).every(([category, values]) => {
      if (values.length === 0) return true;
      
      switch (category) {
        case 'technology':
          return values.some(tech => project.technologies.includes(tech));
        case 'type':
          // Map project categories to filter types
          const typeMapping = {
            'E-commerce & AI': 'ai-platform',
            'FinTech & Analytics': 'dashboard',
            'Healthcare & IoT': 'mobile-app'
          };
          return values.includes(typeMapping[project.category]);
        case 'industry':
          const industryMapping = {
            'E-commerce & AI': 'ecommerce',
            'FinTech & Analytics': 'fintech',
            'Healthcare & IoT': 'healthcare'
          };
          return values.includes(industryMapping[project.category]);
        case 'complexity':
          return values.includes('high'); // All projects are high complexity
        default:
          return true;
      }
    });
  });

  const handleFilterChange = (category, value, checked) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: checked
        ? [...prev[category], value]
        : prev[category].filter(item => item !== value)
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      technology: [],
      type: [],
      industry: [],
      complexity: []
    });
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setModalMode('details');
    setIsModalOpen(true);
  };

  const handleViewDemo = (project) => {
    window.open(`https://demo.${project.title.toLowerCase().replace(/\s+/g, '-')}.com`, '_blank');
  };

  const handleViewCode = (project) => {
    setSelectedProject(project);
    setIsCodeLabOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleCloseCodeLab = () => {
    setIsCodeLabOpen(false);
    setSelectedProject(null);
  };

  return (
    <>
      <Helmet>
        <title>Project Dimensions - CyberDev Nexus Portfolio</title>
        <meta name="description" content="Explore an immersive 3D gallery of cutting-edge projects featuring AI, blockchain, and next-generation web technologies. Dive deep into technical architectures and live demonstrations." />
        <meta name="keywords" content="portfolio, projects, web development, AI, blockchain, React, cyberpunk, futuristic" />
      </Helmet>

      <div className="min-h-screen bg-background text-foreground">
        <Header />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0 particle-bg" />
          
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-xl flex items-center justify-center cyber-glow-intense">
                  <Icon name="Layers" size={32} color="#ffffff" />
                </div>
              </div>
              
              <h1 className="font-orbitron text-4xl md:text-6xl font-bold text-foreground mb-6">
                Project <span className="text-brand-cyan">Dimensions</span>
              </h1>
              
              <p className="font-inter text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
                Step into an immersive 3D gallery where cutting-edge projects exist as dimensional portals. 
                Explore live demonstrations, examine source code, and discover the technical mastery behind 
                each digital creation.
              </p>
            </motion.div>

            {/* Portfolio Statistics */}
            <ProjectStats stats={portfolioStats} />
          </div>
        </section>

        {/* Filter Panel */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8">
          <FilterPanel
            filters={filterConfig}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </section>

        {/* Projects Gallery */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
          {filteredProjects.length === 0 ? (
            <motion.div
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="w-24 h-24 bg-surface/30 rounded-full flex items-center justify-center mx-auto mb-6 cyber-border">
                <Icon name="Search" size={32} className="text-text-secondary" />
              </div>
              <h3 className="font-exo text-2xl font-bold text-foreground mb-4">
                No Projects Found
              </h3>
              <p className="text-text-secondary mb-8">
                Try adjusting your filters to discover more projects.
              </p>
              <Button
                variant="outline"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={handleClearFilters}
                className="cyber-border hover:cyber-glow"
              >
                Reset Filters
              </Button>
            </motion.div>
          ) : (
            <>
              {/* Results Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="font-exo text-2xl font-bold text-foreground">
                    Project Gallery
                  </h2>
                  <p className="text-text-secondary">
                    Showing {filteredProjects.length} of {projects.length} projects
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <span className="text-text-secondary text-sm">Sort by:</span>
                  <select className="bg-surface/50 border border-border rounded-lg px-3 py-2 text-foreground focus:border-brand-cyan focus:outline-none">
                    <option value="featured">Featured First</option>
                    <option value="newest">Newest First</option>
                    <option value="complexity">Complexity</option>
                    <option value="technology">Technology</option>
                  </select>
                </div>
              </div>

              {/* Projects Grid */}
              <motion.div
                className={`grid gap-8 ${
                  viewMode === 'grid' ?'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                    : viewMode === 'list' ?'grid-cols-1' :'grid-cols-1 md:grid-cols-2'
                }`}
                layout
              >
                <AnimatePresence>
                  {filteredProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onViewDetails={handleViewDetails}
                      onViewDemo={handleViewDemo}
                      onViewCode={handleViewCode}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>
            </>
          )}
        </section>

        {/* Call to Action */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-cyan/10 via-brand-purple/10 to-brand-green/10" />
          
          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-orbitron text-3xl md:text-4xl font-bold text-foreground mb-6">
                Ready to Build the <span className="text-brand-cyan">Future</span>?
              </h2>
              
              <p className="font-inter text-xl text-text-secondary mb-8 leading-relaxed">
                Let's collaborate on your next groundbreaking project. From concept to deployment, 
                I'll help you create digital experiences that push the boundaries of what's possible.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  size="lg"
                  iconName="MessageSquare"
                  iconPosition="left"
                  className="cyber-glow hover:cyber-glow-intense"
                >
                  Start a Project
                </Button>
                
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Calendar"
                  iconPosition="left"
                  className="cyber-border hover:cyber-border-active hover:cyber-glow"
                >
                  Schedule Consultation
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Project Detail Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          mode={modalMode}
        />

        {/* Code Laboratory Modal */}
        <CodeLaboratory
          project={selectedProject}
          isOpen={isCodeLabOpen}
          onClose={handleCloseCodeLab}
        />
      </div>
    </>
  );
};

export default ProjectDimensionsPortfolio;